<template>
  <div class="dashboard-block" v-if="!booking.is_offer">
    <h2 v-if="$userIsAdmin || $userIsManager">
      {{ $starLabel }} List

      <a
        data-toggle="modal"
        data-target="#modal-add-talent"
        data-backdrop="static"
        class="btn btn-sm pull-right"
        v-if="userCanAddTalentResponses && $userIsAdmin">
        <i class="fa fa-plus"></i>
        <template>Add {{ $starLabelPlural }}</template>
      </a>
      <a
        data-toggle="modal"
        data-target="#modal-add-talent-manager"
        data-backdrop="static"
        class="btn btn-sm pull-right"
        v-if="userCanAddTalentResponses && $userIsManager">
        <i class="fa fa-plus"></i>
        <template>Apply For {{ $starLabelPlural }}</template>
      </a>
    </h2>
    <h2 v-else>
      {{ $starLabelPlural }} who have responded to your {{ $t('request') }}
    </h2>

    <div v-if="haveNoTalentResponses" class="text-center alert alert-default">
      <template v-if="$userIsAdmin || $userIsManager">
        There are no {{ $starLabelPlural }} responses.
      </template>
      <template v-else>
        No {{ $starLabelPlural }} have responded yet.
      </template>
    </div>

    <ul
      v-else
      class="item-detail item-detail-simple"
      :class="{
        'item-detail-response': $userIsAdmin || $userIsManager,
        'item-detail-your-stars': $userIsClient
      }">
      <booking-responses-list-item
        v-for="talent in talentResponded"
        v-if="!(talent.response.shortlisted === false && talent.response.status === 'declined' && talent.response.decline_reason === 'app')"
        :key="talent.id"
        :talent="talent"
        :booking="booking" />
    </ul>

    <ladda-button
      v-if="userCanContinueBooking"
      ref="continue"
      class="btn btn-primary pull-right btn-continue-star-low"
      :click="handleContinueBookingRequestCallback">

      <template v-if="bookingHasExactTalent">Complete Your Booking</template>
      <template v-else>
        Complete Your Booking With {{ bookingAttendingTalentCount }} {{ bookingAttendingTalentCount > 1 ? $starLabelPlural : $starLabel }}
      </template>

      <i class="fa fa-chevron-right"></i>
    </ladda-button>

    <div class="clearfix"></div>

    <booking-add-talent-modal
      key="1"
      v-if="$userIsAdmin"/>

    <booking-add-talent-modal-manager
      v-if="$userIsManager"/>

    <booking-terms-and-conditions-modal
      ref="terms"
      :booking="booking"
      :talents="bookingAttendingTalent"
      :property-mutator="setRequestProperty"/>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { helpers } from 'mixins/bookings'
import { filter, sumBy } from 'lodash'
import { alert } from 'utils'
import BookingAddTalentModal from './BookingAddTalentModal'
import BookingAddTalentModalManager from './BookingAddTalentModalManager'
import BookingResponsesListItem from './BookingResponsesListItem'
import BookingTermsAndConditionsModal from './BookingTermsAndConditionsModal'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, OpportunityTracker],

  components: {
    BookingAddTalentModal,
    BookingAddTalentModalManager,
    BookingResponsesListItem,
    BookingTermsAndConditionsModal
  },

  props: {
    booking: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      canAcceptTermsAndConditions: false,
      closeBooking: true
    }
  },

  computed: {
    /**
     * Get the talent that have responded based on user role.
     *
     * If admin or manager show all talent that have responded.
     *
     * If client only show talent that have applied or shortlisted
     * talent that have declined on the booking request.
     *
     * @return array
     */
    talentResponded () {
      return filter(this.booking.talent, (talent) => {
        if (this.$userIsAdmin || this.$userIsManager) {
          return true
        }
        return talent.response.status === 'applied' || talent.response.status === 'declined' || (talent.response.status === 'pending' && talent.response.shortlisted && this.bookingIsStandardPackage)
      })
    },

    /**
     * Determine if booking request has talent responses.
     *
     * @return bool
     */
    haveNoTalentResponses () {
      return this.talentResponded.length === 0
    },

    /**
     * Determine if booking request can be continued to a scheduled booking.
     *
     * @return boolean
     */
    userCanContinueBooking () {
      if (this.bookingAttendingTalentCount <= 0) {
        return false
      }
      if (this.$userIsAdmin) {
        return true
      }
      if (this.$userIsClient) {
        return this.$clientsCanMakeBookings
      }
      return false
    },

    /**
     * Determine if booking has exact talent.
     *
     * @return bool
     */
    bookingHasExactTalent () {
      return this.bookingAttendingTalentCount === this.booking.required_stars
    },

    /**
     * Get number of attending talent.
     *
     * @return int
     */
    bookingAttendingTalentCount () {
      return this.bookingAttendingTalent.length
    },

    userCanAddTalentResponses () {
      if (this.$userIsManager) {
        return this.bookingTypeIsBookingRequest &&
          this.bookingIsPending &&
          (this.bookingIsPublic || (this.booking.talent_notified.length && this.$billboardModuleEnabled))
      }
      if (this.$userIsNonGlobalAdmin) {
        return this.booking.region_id === this.$user.region_id
      }

      return true
    }
  },

  watch: {
    /**
     * When client are selecting talent that have applied automatically prompt them
     * to continue the request if required stars is 1.
     *
     * @return void
     */
    talentResponded () {
      const talentClientHasAccepted = sumBy(this.talentResponded, (talent) => {
        if (talent.response.client_accepted) {
          return 1
        }

        return 0
      })

      if (
        this.$userIsClient &&
        talentClientHasAccepted > 0 &&
        talentClientHasAccepted === this.booking.required_stars &&
        this.booking.required_stars === 1
      ) {
        this.$nextTick(() => {
          this.$refs.continue.$el.click()
        })
      }
    }
  },

  methods: {
    ...mapActions('booking/request', ['closeBookingRequest', 'continueBookingRequest', 'setRequestProperty', 'getBookingRequest']),

    /**
     * Continue a booking request through to a scheduled booking.
     *
     * Confirms with the user if they are attempting to continue with less than required talent.
     *
     * @return Promise
     */
    handleContinueBookingRequestCallback () {
      const confirmAlertOptions = {
        icon: 'warning',
        dangerMode: false,
        title: 'Accepting more Talent?',
        text: 'Did you want to finalise your booking, or confirm selected Talent and leave open for other applicants?',
        buttons: {
          confirm: {
            text: 'Finalise',
            value: 'finalise'
          },
          continue: {
            text: 'Confirm Talent & leave open',
            className: 'btn-primary',
            value: 'continue'
          }
        }
      }

      const bookingPromise = new Promise((resolve, reject) => {
        if (this.bookingHasExactTalent) {
          resolve()
        } else {
          return alert.confirm(confirmAlertOptions)
            .then((result) => {
              this.trackEvent(this.analytics.V2.JobDetailsConfirmEvent)
              this.closeBooking = (result === 'finalise')
            })
            .then(resolve)
            .catch(reject)
        }
      })

      return bookingPromise
        .then(this.bookingTermsAndConditions)
        .then(() => {
          this.trackEvent(this.analytics.V2.JobStarSelectionConfirmEvent)
          if (this.closeBooking) {
            return this.closeBookingRequest({ id: this.booking.id })
          } else {
            return this.continueBookingRequest({ id: this.booking.id })
          }
        })
        .then((response) => {
          this.bookingWasSuccessfulScheduled()
          return response
        })
        .then((response) => { this.redirectToScheduledBooking(response) })
    },

    visibleResponse () {
      return false
    },

    /**
     * Show the booking terms and conditions.
     *
     * @return Promise
     */
    bookingTermsAndConditions () {
      return new Promise((resolve, reject) => {
        this.$refs.terms.show({ resolve, reject })
      })
    },

    /**
     * Alert that booking was successfully scheduled.
     *
     * @return Promise
     */
    bookingWasSuccessfulScheduled () {
      alert.timedSuccess({
        buttons: {
          confirm: {
            text: 'Continue'
          }
        },
        timer: 3000,
        title: 'Congrats!',
        text: 'You\'ve successfully scheduled your booking.'
      })
    },

    /**
     * Redirect to the scheduled booking.
     *
     * @return void
     */
    redirectToScheduledBooking (response) {
      if (response.data.payments && response.data.payments.length > 0) {
        this.$router.push({ name: 'payments.show',
          params: {
            bookingId: response.data.id,
            action: 'view',
            paymentId: response.data.payments[0].id
          }
        })
      } else {
        this.$router.push({ name: 'bookings.show',
          params: {
            id: response.data.id
          }
        })
      }
    }
  }
}
</script>
